import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  LocationOn,
  School,
  People,
  Star,
  StarBorder,
  Compare,
  Launch,
  AttachMoney,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UniversityCard = ({ 
  university, 
  onAddToComparison, 
  isInComparison = false, 
  onToggleFavorite,
  isFavorite = false,
  loading = false 
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  if (loading) {
    return (
      <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={200} />
        <CardContent>
          <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" width={60} height={20} sx={{ mr: 1, display: 'inline-block' }} />
            <Skeleton variant="rectangular" width={80} height={20} sx={{ display: 'inline-block' }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const handleViewDetails = () => {
    navigate(`/universities/${university.ShortName}`);
  };

  const handleQuickApply = () => {
    navigate('/services', { state: { selectedUniversity: university } });
  };

  const getTuitionRange = () => {
    const tuition = university.tuition;
    if (!tuition || !tuition.undergraduate) return null;
    const { undergraduate } = tuition;
    if (!undergraduate.min || !undergraduate.max) return null;
    
    // 检查是否为无效值（字符串 '-'、'null'、null、undefined）
    if (undergraduate.min === '-' || undergraduate.min === 'null' || 
        undergraduate.max === '-' || undergraduate.max === 'null') return null;
    
    // 转换为数字并检查是否为有效数字
    const min = Number(undergraduate.min);
    const max = Number(undergraduate.max);
    
    if (isNaN(min) || isNaN(max)) return null;
    // 如果最小值或最大值都为 0，则不显示
    if (min === 0 && max === 0) return null;
    
    return `RM ${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  const getRankingDisplay = () => {
    if (university.rankingValue !== null && university.rankingValue !== undefined) {
      return `QS第${university.rankingValue}名`;
    }
    if (university.ranking && university.ranking !== '-' && university.ranking !== 'null') {
      return `QS第${university.ranking}名`;
    }
    return null;
  };

  // 检查数据是否存在
  const hasLocation = university.location && (university.location.state || university.location.city);
  const hasEstablished = university.established && university.established !== '-' && university.established !== 'null';
  const hasStudents = university.students?.total && university.students.total !== '-';
  const hasTuition = getTuitionRange() !== null;
  const hasRanking = getRankingDisplay() !== null;

  return (
    <Card 
      elevation={2} 
      sx={{ 
        height: '100%', 
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          elevation: 6,
          transform: 'translateY(-4px)',
        },
        border: isInComparison ? '2px solid' : '1px solid',
        borderColor: isInComparison ? 'primary.main' : 'divider',
      }}
    >
      {/* 大学图片 */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <CardMedia
          component="img"
          height="200"
          image={imageError ? '/images/placeholder-university.jpg' : university.image}
          alt={university.name}
          onError={() => setImageError(true)}
          sx={{ objectFit: 'cover' }}
        />
        
        {/* 收藏按钮 */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(255,255,255,0.9)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (onToggleFavorite) onToggleFavorite(university.id);
          }}
        >
          {isFavorite ? <Star color="warning" /> : <StarBorder />}
        </IconButton>

        {/* 排名标签 */}
        {hasRanking && (
          <Chip
            label={getRankingDisplay()}
            size="small"
            color="primary"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      <CardContent sx={{ 
        flexGrow: 1, 
        pb: 1, 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        {/* 内容区域 - 自动填充空间 */}
        <Box sx={{ flexGrow: 1 }}>
          {/* 大学名称 */}
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.3 }}>
            {university.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {university.nameEn}
          </Typography>

          {/* 基本信息 */}
          {(hasLocation || hasEstablished || hasStudents || hasTuition || university.type) && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {hasLocation && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {[university.location.state, university.location.city].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
              </Grid>
            )}
            {(university.type || hasEstablished) && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <School sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {[
                      university.type,
                      hasEstablished ? `成立于${university.established}年` : null
                    ].filter(Boolean).join(' • ')}
                  </Typography>
                </Box>
              </Grid>
            )}
            {hasStudents && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <People sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    在校生 {university.students.total.toLocaleString()} 人
                  </Typography>
                </Box>
              </Grid>
            )}
            {hasTuition && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoney sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {getTuitionRange()}/年
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, flexShrink: 0 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleViewDetails}
              startIcon={<Launch />}
            >
              详情
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleQuickApply}
              sx={{ fontWeight: 600 }}
            >
              申请
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={() => onAddToComparison && onAddToComparison(university)}
              startIcon={<Compare />}
              disabled={isInComparison}
              sx={{ mt: 0.5 }}
            >
              {isInComparison ? '已加入对比' : '加入对比'}
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
};

export default UniversityCard;
